import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getOl } from '../../../../api/ols/get-ol'
import { getPeriods } from '../../../../api/periods/get-periods'
import { getProducts } from '../../../../api/products/get-products'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  CurrencyEnum,
  currencyEnumMapping
} from '../../../../lib/types/enums/common/currency.enum'
import {
  HostFamilyPriceTypeEnum,
  hostFamilyPriceTypeEnumMapping
} from '../../../../lib/types/enums/ol/host-family-price-type.enum'
import { OLHostFamilyPricePerNightInput } from '../../../../lib/types/models/ol/host-family-price-per-night/ol-host-family-price-per-night-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<OLHostFamilyPricePerNightInput, true> & {
  mode: 'create' | 'update' | 'duplicate'
  olId: number
  className?: string
  innerBoxClassName?: string
}

const formSchema: ZodType<Omit<OLHostFamilyPricePerNightInput, 'currency'>> = z.object({
  appliedToHost: createEnumSchema({ fieldName: 'appliqué à', enumType: HostFamilyPriceTypeEnum }),
  productIds: createNumberArraySchema('produits', { isInteger: true, minimumNumberOfItems: 1 }),
  periodIds: createNumberArraySchema('périodes', { isInteger: true, minimumNumberOfItems: 1 }),
  fa1ePrice: createNumberSchema('fa1ePrice', { isPositive: true, nullish: true }),
  fa2ePrice: createNumberSchema('fa2ePrice', { isPositive: true, nullish: true }),
  visitPackPerWeekPrice: createNumberSchema('visitPackPerWeekPrice', {
    isPositive: true,
    nullish: true
  }),
  otoPerHourPrice: createNumberSchema('otoPerHourPrice', { isPositive: true, nullish: true })
})

const OlHostFamilyPriceForm: React.FC<Props> = ({
  mode,
  className,
  defaultValues,
  olId,
  innerBoxClassName,
  ...validationButtonProps
}) => {
  const form = useForm<OLHostFamilyPricePerNightInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { t } = useTranslate()

  const { data } = useQuery({
    queryKey: ['ol', olId],
    queryFn: () => getOl(olId),
    select: (ol) => ol.currency
  })

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const currency = data ?? form.formState.defaultValues?.currency ?? CurrencyEnum.EUR

  return (
    <Form {...form}>
      <form
        className={className}
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
      >
        <InnerBox className={cn('rounded-2xl', innerBoxClassName)}>
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            {mode === 'create'
              ? 'Ajouter une rémunération Famille-hôtesse'
              : mode === 'update'
                ? 'Modifier une rémunération Famille-hôtesse'
                : null}
          </Typography>
          <div className="h-5" />
          <div className={'flex flex-col gap-3'}>
            <FormField
              control={form.control}
              name={'appliedToHost'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      label="S'applique à"
                      options={Object.values(HostFamilyPriceTypeEnum).map((appliedTo) => ({
                        label: t(hostFamilyPriceTypeEnumMapping[appliedTo].label),
                        value: appliedTo
                      }))}
                      selectedOption={getSelectedEnumOption(
                        field.value,
                        hostFamilyPriceTypeEnumMapping,
                        t
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'productIds'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      required
                      label="Produit(s)"
                      variant="primary"
                      searchable
                      options={
                        products?.data.map((product) => ({
                          label: product.name,
                          value: product.id
                        })) ?? []
                      }
                      selectedOptions={
                        field.value?.map((productId) => ({
                          label:
                            products?.data.find((product) => product.id === productId)?.name ?? '',
                          value: productId
                        })) ?? []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'periodIds'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      required
                      label="Période(s)"
                      variant="primary"
                      options={
                        periods?.data.map((period) => ({
                          label: period.name,
                          value: period.id
                        })) ?? []
                      }
                      selectedOptions={
                        field.value?.map((periodId) => ({
                          label:
                            periods?.data.find((period) => period.id === periodId)?.name ?? '',
                          value: periodId
                        })) ?? []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name={'fa1ePrice'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type="number"
                        label={`Prix par nuit FA1E (${currencyEnumMapping[currency].symbol})`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'fa2ePrice'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type="number"
                        label={`Prix par nuit FA2E (${currencyEnumMapping[currency].symbol})`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'otoPerHourPrice'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type="number"
                        label="Prix par heure de cours (OTO)"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'visitPackPerWeekPrice'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type="number"
                        label="Supplément pack visites par semaine (OTO)"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default OlHostFamilyPriceForm
